// SPDX-License-Identifier: AGPL-3.0-or-later

import GameMode from '@app/features/accessibility/state/GameMode';
import {reaction} from 'mobx';
import {useSyncExternalStore} from 'react';

function gameModeHook<T>(read: () => T, serverSnapshot: T): () => T {
	const listeners = new Set<() => void>();
	let stopReaction: (() => void) | null = null;
	const subscribe = (listener: () => void): (() => void) => {
		listeners.add(listener);
		stopReaction ??= reaction(read, () => {
			for (const notify of listeners) notify();
		});
		return () => {
			listeners.delete(listener);
			if (listeners.size > 0) return;
			stopReaction?.();
			stopReaction = null;
		};
	};
	const getServerSnapshot = (): T => serverSnapshot;
	return () => useSyncExternalStore(subscribe, read, getServerSnapshot);
}

// Micup: what keeps animated images still, through one store subscription per caller: game mode (the window is not
// focused) wins over lite mode.
export const useGameModeMotionHold = gameModeHook(
	(): 'game' | 'lite' | null => (GameMode.active ? 'game' : GameMode.lightMode ? 'lite' : null),
	null,
);
// Micup: lite mode (on with the window in front too).
export const useGameModeLight = gameModeHook(() => GameMode.lightMode, false);
